import { useMemo, useState } from "react";
import { Check, Copy, MessageSquareText, Search } from "lucide-react";
import { toast } from "sonner";
import { REPLY_BANK } from "@/data/replies";

const CopyBtn = ({ text, label, testId }) => {
  const [done, setDone] = useState(false);
  const copy = async () => { await navigator.clipboard.writeText(text); setDone(true); toast.success("Reply copied"); setTimeout(() => setDone(false), 1500); };
  return (
    <button type="button" onClick={copy} data-testid={testId} className={`inline-flex items-center gap-1.5 rounded-full h-8 px-3 text-xs font-medium transition-colors duration-300 ${done ? "bg-[#1F1A17] text-[#FBF8F4]" : "border border-[#E8DDD3] bg-white text-[#1F1A17] hover:border-[#1F1A17]"}`}>
      {done ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {done ? "Copied" : label}
    </button>
  );
};

export const ReplyBank = () => {
  const [cat, setCat] = useState(REPLY_BANK[0].key);
  const [q, setQ] = useState("");
  const items = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (needle) return REPLY_BANK.flatMap((c) => c.items.filter((i) => `${i.q} ${i.short} ${i.long}`.toLowerCase().includes(needle)).map((i) => ({ ...i, cat: c.title })));
    return REPLY_BANK.find((c) => c.key === cat).items;
  }, [cat, q]);

  return (
    <section className="mt-12" data-testid="reply-bank">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="h-9 w-9 rounded-xl grid place-items-center bg-[#1F1A17] text-[#FBF8F4]"><MessageSquareText className="h-4 w-4" /></span>
          <div>
            <h2 className="font-serif text-2xl tracking-tight text-[#1F1A17]">Comment reply bank</h2>
            <p className="text-xs text-[#685F58]">Ready-to-paste answers for the questions people ask under posts. Short fits a comment; long is for DMs.</p>
          </div>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9C421E] border border-dashed border-[#B4532A]/40 rounded-full px-3 py-1.5" data-testid="reply-bank-review-badge">Review claims &amp; prices before use</span>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        {REPLY_BANK.map((c) => (
          <button key={c.key} onClick={() => { setCat(c.key); setQ(""); }} data-testid={`reply-cat-${c.key}`} aria-selected={cat === c.key && !q}
            className={`h-9 px-4 rounded-full text-xs font-medium transition-colors duration-300 ${cat === c.key && !q ? "bg-[#1F1A17] text-[#FBF8F4]" : "border border-[#E8DDD3] bg-white text-[#685F58] hover:text-[#1F1A17]"}`}>{c.title} <span className="opacity-60">{c.items.length}</span></button>
        ))}
        <label className="relative ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#685F58]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search replies…" data-testid="reply-search" className="h-9 w-56 rounded-full border border-[#E8DDD3] bg-white pl-9 pr-3 text-xs text-[#1F1A17] outline-none focus:border-[#1F1A17]" />
        </label>
      </div>

      <div className="mt-5 space-y-3">
        {items.length === 0 && <p className="text-sm text-[#685F58]" data-testid="reply-empty">Nothing matches — try another word.</p>}
        {items.map((i, idx) => (
          <article key={i.q} className="rounded-2xl border border-[#E8DDD3] bg-white p-5" data-testid={`reply-item-${idx}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <h3 className="font-serif text-xl tracking-tight text-[#1F1A17]">{i.q} {i.cat && <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#685F58]">{i.cat}</span>}</h3>
              <div className="flex gap-2">
                <CopyBtn text={i.short} label="Copy short" testId={`reply-copy-short-${idx}`} />
                <CopyBtn text={i.long} label="Copy long" testId={`reply-copy-long-${idx}`} />
              </div>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-[#1F1A17]"><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#B4532A] mr-2">Short</span>{i.short}</p>
            <p className="mt-2 text-sm leading-relaxed text-[#685F58]"><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#B4532A] mr-2">Long</span>{i.long}</p>
          </article>
        ))}
      </div>
    </section>
  );
};
